import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import AppPaths from '../../../AppPaths';
import ImageGallery from '../../../components/ImageGallery/ImageGallery';
import * as events from '../../../lib/analytics/events';
import { MARTINGALE_BAND_PRODUCT_ID } from '../../../lib/product';
import { cartActions as storeCartActions } from '../../../reducers/storeCart';
import * as types from '../../../types';
import { Series } from '../../../types/Series';
import AddToBagButton from '../components/AddToBagButton';
import BandSeriesToggle from '../components/BandSeriesToggle';
import BandVariantOptions from '../components/BandVariantOptions';
import ProductTitle from '../components/ProductTitle';
import SoldOutBanner from '../components/SoldOutBanner';
import useIsSkuBlocked from '../hooks/useIsSkuBlocked';
import useVariantSelector from '../hooks/useVariantSelector';
import styles from '../ProductDetails.module.scss';
import { collarProductIsSupportedInCountry, useLocale } from '../../../contexts/LocaleContext';
import { series2CollarId } from '../../../types';

interface OnChangeHandlerProps {
  priceInCents: number;
  productName: string;
  currency: string;
}

function useOnChangeHandlers({ priceInCents, productName, currency }: OnChangeHandlerProps) {
  const onColorChange = useCallback(
    (newVariant: types.IVariant) => {
      events.martingaleBandColorSwitch({
        color: newVariant.options.color,
        priceInCents,
        productName,
        size: newVariant.options.size,
        sku: newVariant.sku,
        currency,
      });
    },
    [priceInCents, productName, currency],
  );

  const onSizeChange = useCallback(
    (newVariant: types.IVariant) => {
      events.martingaleBandSizeSwitch({
        color: newVariant.options.color,
        priceInCents,
        productName,
        size: newVariant.options.size,
        sku: newVariant.sku,
        currency,
      });
    },
    [priceInCents, productName, currency],
  );

  const onSelectedVariantChange = useCallback(
    (newVariant: types.IVariant) => {
      events.martingaleBandVariantSwitch({
        color: newVariant.options.color,
        priceInCents,
        productName,
        size: newVariant.options.size,
        sku: newVariant.sku,
        currency,
      });
    },
    [priceInCents, productName, currency],
  );

  return {
    onColorChange,
    onSizeChange,
    onSelectedVariantChange,
  };
}

interface MartingaleBandDetailsProps {
  priceInCents: number;
  productName: string;
  variants: types.IVariant[];
}

export default function MartingaleBandDetails({ priceInCents, productName, variants }: MartingaleBandDetailsProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { country, currency } = useLocale();

  const { onColorChange, onSizeChange, onSelectedVariantChange } = useOnChangeHandlers({
    priceInCents,
    productName,
    currency,
  });

  const { changeVariantOptions, selectedVariant } = useVariantSelector({
    onColorChange,
    onSelectedVariantChange,
    onSizeChange,
    variants,
    useQueryParams: true,
  });
  const isBlocked = useIsSkuBlocked(selectedVariant.sku);

  useEffect(() => {
    events.martingaleBandViewed();
  }, []);

  if (!collarProductIsSupportedInCountry(series2CollarId, country)) {
    return <Redirect to={AppPaths.Products('additional-band')} />;
  }

  return (
    <>
      <div className={styles.productContainer}>
        <div className={styles.imageGalleryContainer}>
          <ImageGallery
            media={[
              {
                type: 'image',
                url: `/product_images/martingale-band/gray.jpg`,
                retinaWidth: 612,
                retinaHeight: 440,
              },
            ]}
          />
        </div>
        <div className={styles.productDetails}>
          <div className={styles.productDetailsHeader}>
            <div className={styles.productDetailsTitle}>
              <ProductTitle productName={productName} price={{ priceInCents }} />
            </div>
            <p className={styles.description}>
              {t(
                'specially_designed_for_dogs_with_narrower_heads_i_e_greyhounds_and_big_pullers_or_flight_risk_pups_who_can_back_out_of_regular_collars_martingales_provide_extra_safety_by_tightening_around_your_dogs_neck_without_choking_when_they_start_to_pull',
              )}
            </p>
          </div>

          {/* Martingale bands are only available for Series 2 so this is a static toggle acting more like a label */}
          <BandSeriesToggle
            seriesProductIdMap={new Map([[Series.Series2, MARTINGALE_BAND_PRODUCT_ID]])}
            selectedSeries={Series.Series2}
            variantOptions={selectedVariant.options}
          />

          <BandVariantOptions
            bandSeries={types.BandSeries.F1}
            changeVariantOptions={changeVariantOptions}
            selectedVariant={selectedVariant}
            variants={variants}
            startSizeAsUnselected={false}
          />

          {isBlocked && <SoldOutBanner />}

          <div className={styles.productAction}>
            <AddToBagButton
              disabled={isBlocked}
              onAddToBag={() => {
                if (isBlocked) {
                  return false;
                }

                events.martingaleBandAddedToBag({
                  color: selectedVariant.options.color,
                  priceInCents,
                  productName,
                  size: selectedVariant.options.size,
                  sku: selectedVariant.sku,
                  currency,
                });

                dispatch(
                  storeCartActions.addSingleLineCartItem({
                    sku: selectedVariant.sku,
                    currency,
                  }),
                );

                history.push(AppPaths.Bag);

                return true;
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
